import html2canvas from 'html2canvas';
import { TicketPayload, renderTicketPreviewHTML } from '../services/ticketService';
import { openWhatsApp } from './whatsappShare';
import { isNativePlatform } from './platform';

/**
 * Genera una imagen JPG de un ticket y la comparte vía Web Share API o WhatsApp.
 * Retorna true si se compartió con éxito, false si fue cancelado por el usuario.
 */
export async function shareTicketAsImage(payload: TicketPayload): Promise<boolean> {
  const { blob } = await generateTicketImage(payload);
  const fileName = `ticket-${payload.ticketCode}.jpg`;
  const file = new File([blob], fileName, { type: 'image/jpeg' });

  if (navigator.share && navigator.canShare) {
    const shareData = { files: [file], title: `Ticket ${payload.ticketCode}` };
    if (navigator.canShare(shareData)) {
      await navigator.share(shareData);
      return true;
    }
  }

  // Fallback: descargar + abrir WhatsApp con texto
  const jpgUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = jpgUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(jpgUrl);
  openWhatsApp(`🎟 Ticket: ${payload.ticketCode}\n💰 Total: ₡${payload.total.toLocaleString()}`);
  return true;
}

/**
 * Genera la imagen JPG del ticket y devuelve tanto el Blob como un dataUrl para preview.
 */
export async function generateTicketImage(payload: TicketPayload): Promise<{ blob: Blob; dataUrl: string }> {
  const html = renderTicketPreviewHTML(payload);
  const native = isNativePlatform();
  const es58 = payload.config.ancho === 58;

  // Configuración separada para preservar mejor la proporción visual real
  const anchoPx = es58
    ? (native ? 308 : 240)
    : (native ? 500 : 430);
  const padding = es58
    ? (native ? '8px 7px 18px' : '6px 6px 14px')
    : (native ? '12px 10px 20px' : '10px 10px 16px');
  const scale = es58 ? (native ? 2.3 : 2) : (native ? 2.6 : 2.35);
  const jpegQuality = native ? 0.95 : 0.92;

  const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/);
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);

  if (!bodyMatch) {
    throw new Error('No se pudo extraer el contenido del ticket');
  }

  // Contenedor temporal off-screen
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.zIndex = '-1';

  const wrapper = document.createElement('div');
  wrapper.className = es58 ? 'ticket-capture-root ticket-capture-58' : 'ticket-capture-root ticket-capture-80';
  wrapper.style.width = `${anchoPx}px`;
  wrapper.style.maxWidth = `${anchoPx}px`;
  wrapper.style.fontFamily = "'Courier New', Courier, monospace";
  wrapper.style.color = '#000';
  wrapper.style.background = '#fff';
  wrapper.style.padding = padding;
  wrapper.style.textAlign = 'left';
  wrapper.style.boxSizing = 'border-box';
  wrapper.style.overflow = 'visible';

  if (styleMatch) {
    const styleEl = document.createElement('style');
    styleEl.textContent = styleMatch[1];
    container.appendChild(styleEl);
  }

  const captureStyle = document.createElement('style');
  captureStyle.textContent = `
    .ticket-capture-root {
      background: #fff;
      color: #000;
      overflow: visible !important;
      text-rendering: geometricPrecision !important;
      -webkit-font-smoothing: antialiased !important;
    }
    .ticket-capture-root .ticket-58,
    .ticket-capture-root .ticket-80 {
      margin: 0 auto !important;
      overflow: visible !important;
      padding-bottom: 8px !important;
    }
    .ticket-capture-root .sep {
      margin: 4px 0 !important;
      height: 0 !important;
      border: 0 !important;
      ${es58 ? 'border-top: 1px dotted #666 !important;' : ''}
      font-size: 0 !important;
      line-height: 0 !important;
      color: transparent !important;
    }
    .ticket-capture-root .seccion,
    .ticket-capture-root .detalle,
    .ticket-capture-root .sorteo,
    .ticket-capture-root .sorteo-destacado { border: 0 !important; }
    .ticket-capture-root .row,
    .ticket-capture-root .meta-row,
    .ticket-capture-root .premio-row,
    .ticket-capture-root .rev-header,
    .ticket-capture-root .rev-row,
    .ticket-capture-root .fila58,
    .ticket-capture-root .rev-head58,
    .ticket-capture-root .rev-row58 { align-items: baseline !important; }
    .ticket-capture-root .ticket-id,
    .ticket-capture-root .line,
    .ticket-capture-root .center-note,
    .ticket-capture-root .nota { margin-bottom: 2px !important; padding-bottom: 2px !important; }
  `;
  container.appendChild(captureStyle);

  wrapper.innerHTML = bodyMatch[1].replace(/<script[\s\S]*?<\/script>/gi, '');

  const bottomSpacer = document.createElement('div');
  bottomSpacer.style.height = native ? '10px' : '8px';
  bottomSpacer.style.background = '#fff';
  bottomSpacer.style.width = '100%';
  wrapper.appendChild(bottomSpacer);

  // Barcode fallback: reemplazar SVG por canvas con texto
  const barcodeSvg = wrapper.querySelector('#ticket-barcode');
  const barcodeWrap = wrapper.querySelector('.barcode-wrap');
  if (barcodeWrap && !payload.esReimpresion) {
    try {
      const barcodeCanvas = document.createElement('canvas');
      barcodeCanvas.width = anchoPx - 16;
      barcodeCanvas.height = 50;
      const ctx = barcodeCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, barcodeCanvas.width, barcodeCanvas.height);
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 14px Courier New';
        ctx.textAlign = 'center';
        ctx.fillText(payload.ticketCode, barcodeCanvas.width / 2, 30);
      }
      if (barcodeSvg) barcodeSvg.remove();
      barcodeWrap.innerHTML = '';
      barcodeWrap.appendChild(barcodeCanvas);
    } catch {
      if (barcodeSvg) barcodeSvg.remove();
    }
  }

  container.appendChild(wrapper);
  document.body.appendChild(container);

  await new Promise(r => setTimeout(r, 100));

  try {
    const canvas = await html2canvas(wrapper, {
      backgroundColor: '#ffffff',
      scale,
      width: anchoPx,
      windowWidth: anchoPx,
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        b => b ? resolve(b) : reject(new Error('Error al generar imagen')),
        'image/jpeg', jpegQuality
      );
    });

    const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
    return { blob, dataUrl };
  } finally {
    document.body.removeChild(container);
  }
}
